// InboxPanel.jsx — v1.1.0 Gmail OAuth + Admin Inbox
//
// Admin-only inbox that pulls Gmail messages for:
//   hello@ / support@ / info@birthdaydealight.com
//
// Three states:
//   1. Disconnected      → "Connect Gmail" button → opens OAuth in new tab
//   2. Connected, empty  → "Sync now" button (nothing synced yet)
//   3. Connected, data   → alias tabs + message list + detail drawer
import { useEffect, useMemo, useState } from "react";
import { API, axios } from "@/api";
import PanelRefreshButton from "./PanelRefreshButton";
import {
  Loader2, Mail, RefreshCw, Unlink, CheckCircle2, Circle, Inbox as InboxIcon, Flag, Send, CornerDownRight,
} from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const ALIAS_TABS = [
  { key: "", label: "All" },
  { key: "hello@birthdaydealight.com", label: "hello@" },
  { key: "support@birthdaydealight.com", label: "support@" },
  { key: "info@birthdaydealight.com", label: "info@" },
];

// v1.1.1 — Priority flag keywords. Client-side triage heuristic. If ANY of
// these appear in subject/snippet/from_email (case-insensitive, word-ish
// boundary for multi-word phrases), the message gets a red flag so the
// admin can scan Flagged Only during high-volume beta periods.
//
// Module-scope const per CODING_STANDARDS.md Frontend Standard #1 —
// never inline a `.map()` / filter source array inside JSX.
const FLAG_KEYWORDS = [
  "bug", "broken", "crash", "refund", "error",
  "not working", "help", "urgent", "wrong", "missing",
];

// Precompile once at module load — case-insensitive, word-aware. Multi-word
// phrases like "not working" are matched as contiguous substrings since \b
// doesn't play nice with internal spaces.
const FLAG_REGEXES = FLAG_KEYWORDS.map((kw) => {
  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Single-word: word-boundary both sides. Multi-word: substring, case-insensitive.
  const pattern = kw.includes(" ") ? escaped : `\\b${escaped}\\b`;
  return new RegExp(pattern, "i");
});

function isFlagged(msg) {
  const haystack = [
    msg?.subject || "",
    msg?.snippet || "",
    msg?.from_email || "",
  ].join(" ");
  return FLAG_REGEXES.some((rx) => rx.test(haystack));
}

export default function InboxPanel() {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [aliasFilter, setAliasFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // iter261 — Conversation filter: show only threads the admin has replied to.
  const [repliedOnly, setRepliedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [openMessage, setOpenMessage] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [error, setError] = useState(null);

  // v1.0.9 iter259 — Reply-from-Inbox state
  const [replyBody, setReplyBody] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState(null);
  const [replySuccess, setReplySuccess] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/admin/inbox/status`);
      setStatus(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const loadMessages = async () => {
    try {
      const params = { limit: 50 };
      if (aliasFilter) params.alias = aliasFilter;
      if (unreadOnly) params.read = false;
      const r = await axios.get(`${API}/admin/inbox`, { params });
      setMessages(r.data.rows || []);
      setTotalUnread(r.data.unread || 0);
      setLastRefreshedAt(Date.now());
    } catch (e) {
      // 401 is normal when not connected — don't surface as error
      if (e?.response?.status !== 401) {
        setError(e?.response?.data?.detail || e.message);
      }
    }
  };

  const refresh = async () => {
    setLoading(true);
    await loadStatus();
    await loadMessages();
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (status?.connected) loadMessages(); }, [aliasFilter, unreadOnly, status?.connected]);

  // Listen for the OAuth popup to postMessage back on success
  useEffect(() => {
    const handler = (ev) => {
      if (ev?.data?.type === "gmail_oauth") {
        // Re-check status; the callback has just stored tokens
        setTimeout(refresh, 500);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectGmail = async () => {
    setError(null);
    try {
      const r = await axios.get(`${API}/oauth/gmail/login`);
      const authUrl = r.data?.auth_url;
      if (!authUrl) throw new Error("no auth_url returned");
      window.open(authUrl, "_blank", "width=520,height=640");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "failed to start OAuth");
    }
  };

  const doSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/inbox/sync`);
      await loadMessages();
      if (r.data?.new_count === 0) {
        // Visual feedback w/o a toast dep — just refresh time
        setLastRefreshedAt(Date.now());
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? Stored messages stay; future syncs will require re-connect.")) return;
    try {
      await axios.delete(`${API}/admin/oauth/gmail/disconnect`);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const openDetail = async (gid) => {
    try {
      const r = await axios.get(`${API}/admin/inbox/${gid}`);
      setOpenMessage(r.data);
      setReplyBody("");
      setReplyError(null);
      setReplySuccess(false);
      if (!r.data.read) {
        await axios.post(`${API}/admin/inbox/${gid}/mark-read`, { read: true });
        setMessages((m) => m.map((x) => (x.gmail_id === gid ? { ...x, read: true } : x)));
        setTotalUnread((u) => Math.max(0, u - 1));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const sendReply = async () => {
    if (!openMessage?.gmail_id) return;
    const body = replyBody.trim();
    if (!body) {
      setReplyError("Reply cannot be empty.");
      return;
    }
    setSendingReply(true);
    setReplyError(null);
    setReplySuccess(false);
    try {
      const r = await axios.post(
        `${API}/admin/inbox/${openMessage.gmail_id}/reply`,
        { body_text: body },
      );
      const newReply = r.data?.reply;
      // Append locally so the drawer shows the reply immediately
      setOpenMessage((m) => ({
        ...m,
        replied: true,
        replies: [...(m?.replies || []), newReply],
      }));
      setMessages((list) => list.map((x) =>
        x.gmail_id === openMessage.gmail_id ? { ...x, replied: true } : x
      ));
      setReplyBody("");
      setReplySuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const reason = typeof detail === "string"
        ? detail
        : detail?.reason || detail?.error || e.message || "send failed";
      setReplyError(reason);
    } finally {
      setSendingReply(false);
    }
  };

  const aliasCounts = useMemo(() => {
    const c = {};
    messages.forEach((m) => {
      c[m.to_alias || "__none"] = (c[m.to_alias || "__none"] || 0) + 1;
    });
    return c;
  }, [messages]);

  // Client-side priority filter (no backend). Flag count + visible rows
  // both derived from the same predicate so the toggle stays consistent.
  const flaggedCount = useMemo(
    () => messages.reduce((n, m) => n + (isFlagged(m) ? 1 : 0), 0),
    [messages],
  );
  // iter261 — replied-thread count for the Conversation filter.
  const repliedCount = useMemo(
    () => messages.reduce((n, m) => n + (m?.replied ? 1 : 0), 0),
    [messages],
  );
  const visibleMessages = useMemo(() => {
    let list = messages;
    if (flaggedOnly) list = list.filter(isFlagged);
    if (repliedOnly) list = list.filter((m) => m?.replied);
    return list;
  }, [messages, flaggedOnly, repliedOnly]);

  if (loading && !status) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="inbox-panel">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <InboxIcon className="w-5 h-5 text-sky-500" />
          <h3 className="text-sm font-bold text-slate-700">Workspace Inbox</h3>
          {totalUnread > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600" data-testid="inbox-unread-count">
              {totalUnread} unread
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status?.connected && (
            <>
              <button
                onClick={doSync}
                disabled={syncing}
                className="text-xs font-bold px-3 py-1.5 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-60 inline-flex items-center gap-1.5"
                data-testid="inbox-sync-btn"
              >
                {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Sync
              </button>
              <button
                onClick={disconnect}
                className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                data-testid="inbox-disconnect-btn"
                title="Disconnect Gmail"
              >
                <Unlink className="w-3 h-3" />
                Disconnect
              </button>
            </>
          )}
          <PanelRefreshButton onRefresh={refresh} lastRefreshedAt={lastRefreshedAt} testId="inbox-refresh-btn" />
        </div>
      </div>

      {/* Connection state */}
      {!status?.connected && (
        <div className="rounded-xl border-2 border-dashed border-sky-200 bg-sky-50 p-6 text-center" data-testid="inbox-connect-card">
          <Mail className="w-8 h-8 text-sky-500 mx-auto mb-2" />
          <p className="text-sm font-bold text-slate-700 mb-1">Connect a Google Workspace account</p>
          <p className="text-xs text-slate-500 mb-4">
            Pulls mail addressed to {(status?.aliases_watched || ALIAS_TABS.slice(1).map((a) => a.key)).join(" · ")}
          </p>
          <button
            onClick={connectGmail}
            className="text-sm font-bold px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 inline-flex items-center gap-2"
            data-testid="inbox-connect-btn"
          >
            <Mail className="w-4 h-4" />
            Connect Gmail
          </button>
        </div>
      )}

      {status?.connected && (
        <>
          <div className="text-[11px] text-slate-500">
            Connected as <span className="font-mono text-slate-700">{status.connected_email || "(unknown)"}</span>
            {status.connected_at && (
              <> · since {new Date(status.connected_at).toLocaleString()}</>
            )}
          </div>

          {/* Alias tabs */}
          <div className="flex flex-wrap gap-1.5" data-testid="inbox-alias-tabs">
            {ALIAS_TABS.map((tab) => {
              const active = aliasFilter === tab.key;
              return (
                <button
                  key={tab.key || "all"}
                  onClick={() => setAliasFilter(tab.key)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition ${
                    active ? "bg-sky-500 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`inbox-tab-${tab.key || "all"}`}
                >
                  {tab.label}
                </button>
              );
            })}
            <label className="ml-2 inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                className="rounded"
                data-testid="inbox-unread-toggle"
              />
              Unread only
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={flaggedOnly}
                onChange={(e) => setFlaggedOnly(e.target.checked)}
                className="rounded"
                data-testid="inbox-flagged-toggle"
              />
              <Flag className="w-3 h-3 text-red-500" />
              Flagged only
              {flaggedCount > 0 && (
                <span className="ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-600" data-testid="inbox-flagged-count">
                  {flaggedCount}
                </span>
              )}
            </label>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={repliedOnly}
                onChange={(e) => setRepliedOnly(e.target.checked)}
                className="rounded"
                data-testid="inbox-replied-toggle"
              />
              <CornerDownRight className="w-3 h-3 text-emerald-500" />
              Replied only
              {repliedCount > 0 && (
                <span className="ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid="inbox-replied-count">
                  {repliedCount}
                </span>
              )}
            </label>
          </div>

          {/* Message list */}
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="inbox-list">
            {visibleMessages.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-400 italic">
                {messages.length === 0
                  ? "No messages yet — hit Sync to pull from Gmail."
                  : repliedOnly
                  ? "No replied threads yet — reply to a message to see it here."
                  : flaggedOnly
                  ? "No flagged messages in this view."
                  : "No messages match the current filter."}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {visibleMessages.map((m) => {
                  const flagged = isFlagged(m);
                  return (
                  <li key={m.gmail_id}>
                    <button
                      onClick={() => openDetail(m.gmail_id)}
                      className={`w-full text-left px-3 py-2.5 hover:bg-sky-50 flex items-start gap-2 ${
                        m.read ? "opacity-75" : "font-semibold"
                      }`}
                      data-testid={`inbox-row-${m.gmail_id}`}
                    >
                      {m.read ? (
                        <CheckCircle2 className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Circle className="w-4 h-4 text-sky-500 fill-sky-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">
                          <span className="font-mono">{m.to_alias?.split("@")[0] || "—"}</span>
                          <span>·</span>
                          <span>{m.from_email}</span>
                        </div>
                        <p className="text-sm text-slate-700 truncate flex items-center gap-1.5">
                          {flagged && (
                            <Flag
                              className="w-3.5 h-3.5 text-red-500 flex-shrink-0 fill-red-500"
                              data-testid={`inbox-flag-${m.gmail_id}`}
                              aria-label="priority keyword detected"
                            />
                          )}
                          <span className="truncate">{m.subject || "(no subject)"}</span>
                        </p>
                        <p className="text-xs text-slate-400 truncate">{m.snippet}</p>
                      </div>
                      {m.received_at && (
                        <span className="text-[10px] text-slate-400 whitespace-nowrap">
                          {new Date(m.received_at).toLocaleDateString()}
                        </span>
                      )}
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-700" data-testid="inbox-error">
          {String(error)}
        </div>
      )}

      {/* Message detail drawer */}
      <Sheet open={!!openMessage} onOpenChange={(o) => !o && setOpenMessage(null)}>
        <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="inbox-detail-drawer">
          {openMessage && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{openMessage.subject || "(no subject)"}</SheetTitle>
                <SheetDescription asChild>
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <div>From <span className="font-mono text-slate-700">{openMessage.from_email}</span> · {openMessage.from_name}</div>
                    <div>To <span className="font-mono text-slate-700">{openMessage.to_alias}</span></div>
                    {openMessage.received_at && (
                      <div>{new Date(openMessage.received_at).toLocaleString()}</div>
                    )}
                  </div>
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4">
                {openMessage.body_html ? (
                  // eslint-disable-next-line react/no-danger
                  <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: openMessage.body_html }} />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{openMessage.body_text || openMessage.snippet || "(empty)"}</pre>
                )}
              </div>

              {/* v1.0.9 iter259 — Existing replies history */}
              {openMessage.replies?.length > 0 && (
                <div className="mt-6 space-y-3" data-testid="inbox-replies-history">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Replies sent ({openMessage.replies.length})
                  </div>
                  {openMessage.replies.map((rep) => (
                    <div
                      key={rep.reply_id}
                      className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
                      data-testid={`inbox-reply-${rep.reply_id}`}
                    >
                      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-700 mb-1">
                        <CornerDownRight className="w-3 h-3" />
                        {rep.sent_by_email || "admin"}
                        <span className="text-slate-400">·</span>
                        {rep.sent_at ? new Date(rep.sent_at).toLocaleString() : ""}
                      </div>
                      <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                        {rep.body_text}
                      </pre>
                    </div>
                  ))}
                </div>
              )}

              {/* v1.0.9 iter259 — Reply compose area */}
              <div className="mt-6 border-t border-slate-200 pt-4" data-testid="inbox-reply-compose">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Reply to <span className="font-mono normal-case text-slate-700">{openMessage.from_email}</span>
                </div>
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  placeholder="Type your reply…"
                  rows={5}
                  maxLength={10000}
                  disabled={sendingReply}
                  className="w-full text-sm border border-slate-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-400 disabled:bg-slate-50"
                  data-testid="inbox-reply-textarea"
                />
                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                  <span className="text-[10px] text-slate-400">
                    From no-reply@birthdaydealight.com · Reply-To hello@birthdaydealight.com
                  </span>
                  <button
                    onClick={sendReply}
                    disabled={sendingReply || !replyBody.trim()}
                    className="text-xs font-bold px-4 py-1.5 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid="inbox-reply-send-btn"
                  >
                    {sendingReply ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Send Reply
                  </button>
                </div>
                {replyError && (
                  <div
                    className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700"
                    data-testid="inbox-reply-error"
                  >
                    {String(replyError)}
                  </div>
                )}
                {replySuccess && !replyError && (
                  <div
                    className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs text-emerald-700"
                    data-testid="inbox-reply-success"
                  >
                    Reply sent — it's logged in the thread above.
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
